import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, ListFilter, Search, Trash2, X } from 'lucide-react';
import { formatApiDateTime } from '../../../helpers/helpers';
import { PortaledLayer } from '../../shared/PortaledLayer';
import RefreshButton from '../../shared/RefreshButton';
import InlineSpinner from '../../shared/InlineSpinner';
import TableLoadingOverlay from '../../shared/TableLoadingOverlay';
import type { LogObjectInfo } from '../types/LogBrowseTypes';
import { getLogFileName, getLogFolderFromKey, formatLogBytes } from '../utils/logBrowseFileUtils';
import type { LogFilesPortalProps } from '../types/LogViewerProps';

export type { LogFilesPortalProps };

// Drag-to-select rubber-band hook (same pattern as DeviceListTable, adapted for
// li[data-file-key] rows).  All DOM mutations go straight to refs — never React
// state — so mousemove causes zero re-renders.
function useDragSelect(params: {
    scrollRef: React.RefObject<HTMLDivElement | null>;
    onSetKeys: (keys: string[]) => void;
}) {
    const { scrollRef, onSetKeys } = params;
    const callbackRef = useRef(onSetKeys);
    useEffect(() => { callbackRef.current = onSetKeys; }, [onSetKeys]);

    const drag = useRef<{
        startX: number; startY: number; startScrollTop: number;
        curX: number; curY: number; active: boolean;
    } | null>(null);
    const rafRef   = useRef<number | null>(null);
    const mouseYRef = useRef(0);

    useEffect(() => {
        const overlay = document.createElement('div');
        Object.assign(overlay.style, {
            position: 'fixed', display: 'none',
            border: '1.5px solid rgba(59,130,246,0.75)',
            background: 'rgba(59,130,246,0.08)',
            pointerEvents: 'none', zIndex: '9999', borderRadius: '2px',
        });
        document.body.appendChild(overlay);

        function showOverlay(x1: number, y1: number, x2: number, y2: number) {
            overlay.style.left   = `${Math.min(x1, x2)}px`;
            overlay.style.top    = `${Math.min(y1, y2)}px`;
            overlay.style.width  = `${Math.abs(x2 - x1)}px`;
            overlay.style.height = `${Math.abs(y2 - y1)}px`;
            overlay.style.display = 'block';
        }
        function hideOverlay() { overlay.style.display = 'none'; }

        function hitTestRows(): string[] {
            const el = scrollRef.current;
            const d  = drag.current;
            if (!el || !d) return [];
            const cr          = el.getBoundingClientRect();
            const curScrollTop = el.scrollTop;
            const cs = d.startY - cr.top + d.startScrollTop;
            const ce = d.curY   - cr.top + curScrollTop;
            const contentTop    = Math.min(cs, ce);
            const contentBottom = Math.max(cs, ce);
            const boxLeft  = Math.min(d.startX, d.curX);
            const boxRight = Math.max(d.startX, d.curX);
            const keys: string[] = [];
            const rows = el.querySelectorAll<HTMLElement>('li[data-file-key]');
            for (const row of rows) {
                const r       = row.getBoundingClientRect();
                const rowTop    = r.top    - cr.top + curScrollTop;
                const rowBottom = r.bottom - cr.top + curScrollTop;
                if (rowBottom > contentTop && rowTop < contentBottom && r.right > boxLeft && r.left < boxRight) {
                    const key = row.dataset.fileKey;
                    if (key) keys.push(key);
                }
            }
            return keys;
        }

        function scrollLoop() {
            const el = scrollRef.current;
            const d  = drag.current;
            if (!el || !d?.active) { rafRef.current = null; return; }
            const cr   = el.getBoundingClientRect();
            const ZONE = 60;
            const my   = mouseYRef.current;
            if (my > cr.bottom - ZONE) el.scrollTop += ((my - (cr.bottom - ZONE)) / ZONE) * 16;
            else if (my < cr.top + ZONE) el.scrollTop -= ((cr.top + ZONE - my) / ZONE) * 16;
            rafRef.current = requestAnimationFrame(scrollLoop);
        }

        function onMouseDown(e: MouseEvent) {
            if (e.button !== 0) return;
            if ((e.target as HTMLElement).closest('input,button,a,select,label')) return;
            const el = scrollRef.current;
            drag.current = {
                startX: e.clientX, startY: e.clientY,
                startScrollTop: el?.scrollTop ?? 0,
                curX: e.clientX, curY: e.clientY, active: false,
            };
            mouseYRef.current = e.clientY;
        }

        function onMouseMove(e: MouseEvent) {
            const d = drag.current;
            if (!d) return;
            mouseYRef.current = e.clientY;
            d.curX = e.clientX; d.curY = e.clientY;
            if (!d.active) {
                if (Math.abs(d.curX - d.startX) < 5 && Math.abs(d.curY - d.startY) < 5) return;
                d.active = true;
                if (!rafRef.current) rafRef.current = requestAnimationFrame(scrollLoop);
            }
            showOverlay(d.startX, d.startY, d.curX, d.curY);
        }

        function onMouseUp() {
            const d = drag.current;
            if (d?.active) {
                const keys = hitTestRows();
                callbackRef.current(keys);
                window.addEventListener('click', (ev) => ev.stopPropagation(), { once: true, capture: true });
            }
            drag.current = null;
            hideOverlay();
            if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
        }

        const container = scrollRef.current;
        container?.addEventListener('mousedown', onMouseDown);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);

        return () => {
            container?.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
            if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
            document.body.removeChild(overlay);
        };
    // All live state is accessed through refs — empty dep array is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}

export default function LogFilesPortal(props: LogFilesPortalProps): JSX.Element {
    const {
        open: _open,
        objects,
        selectedKeys,
        selectedKey,
        busyList,
        busyBulkDownload,
        bulkProgress,
        filesScopeHint,
        onClose,
        onRefreshList,
        onToggleKey,
        onReplaceSelectedKeys,
        onSelectKey,
        onOpenPreview,
        onDownload,
        onDelete,
        busyBulkDelete = false,
    } = props;

    const [filterText, setFilterText] = useState('');
    const scrollRef = useRef<HTMLDivElement>(null);

    const filteredObjects = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        if (!q) return objects;
        return objects.filter((o) => (o.key ?? '').toLowerCase().includes(q));
    }, [objects, filterText]);

    const allSelected =
        filteredObjects.length > 0 && filteredObjects.every((o) => selectedKeys.has(o.key));
    const someSelected = filteredObjects.some((o) => selectedKeys.has(o.key));

    const onToggleSelectAll = useCallback(
        (checked: boolean) => {
            onReplaceSelectedKeys(checked ? filteredObjects.map((o) => o.key) : []);
        },
        [filteredObjects, onReplaceSelectedKeys],
    );

    useDragSelect({ scrollRef, onSetKeys: onReplaceSelectedKeys });

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const hasSelection = selectedKeys.size > 0;

    return (
        <PortaledLayer>
            <div
                className="fixed inset-0 z-[60] flex flex-col bg-black/40 p-4"
                style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
                onClick={(e) => {
                    if (e.target === e.currentTarget) onClose();
                }}
            >
                <div className="flex flex-1 min-h-0 flex-col overflow-hidden rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl">

                    {/* ── Header ── */}
                    <div className="flex shrink-0 items-center gap-3 border-b border-slate-300 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-900 px-4 py-3">
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                                Log Files
                            </div>
                            <div className="truncate text-xs text-gray-500 dark:text-slate-400">
                                {filesScopeHint}
                            </div>
                        </div>
                        <div className="relative shrink-0 w-52">
                            <Search
                                className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400 dark:text-slate-500"
                                aria-hidden
                            />
                            <input
                                className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 pl-7 pr-3 py-1.5 text-sm text-gray-900 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500/20"
                                value={filterText}
                                onChange={(e) => setFilterText(e.target.value)}
                                placeholder="Filter files…"
                            />
                        </div>
                        <RefreshButton
                            busy={busyList}
                            onClick={onRefreshList}
                            className="shrink-0 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50"
                            busyText="Loading…"
                        />
                        <button
                            type="button"
                            className="shrink-0 rounded-md p-1.5 text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800"
                            onClick={onClose}
                            aria-label="Close file viewer"
                        >
                            <X className="h-4 w-4" aria-hidden />
                        </button>
                    </div>

                    {/* ── Sub-header: count + select-all ── */}
                    <div className="flex shrink-0 items-center gap-4 border-b border-gray-100 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-800/50 px-4 py-2 text-xs text-gray-600 dark:text-slate-400">
                        <label className="inline-flex cursor-pointer select-none items-center gap-1.5">
                            <input
                                type="checkbox"
                                checked={allSelected}
                                ref={(el) => {
                                    if (el) el.indeterminate = !allSelected && someSelected;
                                }}
                                onChange={(e) => onToggleSelectAll(e.target.checked)}
                                disabled={filteredObjects.length === 0}
                            />
                            <span>Select all</span>
                        </label>
                        <span className="flex items-center gap-1">
                            <ListFilter className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500" aria-hidden />
                            {filteredObjects.length} file{filteredObjects.length !== 1 ? 's' : ''}
                        </span>
                        {hasSelection && (
                            <span className="ml-auto font-semibold text-blue-600 dark:text-blue-400">
                                {selectedKeys.size} selected
                            </span>
                        )}
                    </div>

                    {/* ── Scrollable file list ── */}
                    <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto select-none">
                        <TableLoadingOverlay loading={busyList} label="Loading…" className="relative min-h-[120px]">
                            {filteredObjects.length === 0 ? (
                                <div className="p-8 text-center text-sm text-gray-500 dark:text-slate-400">
                                    {busyList ? 'Loading…' : 'No files found.'}
                                </div>
                            ) : (
                                <ul className="divide-y divide-gray-100 dark:divide-slate-800">
                                    {filteredObjects.map((o) => {
                                        const checked = selectedKeys.has(o.key);
                                        const active  = o.key === selectedKey;
                                        const file    = getLogFileName(o.key);
                                        return (
                                            <li
                                                key={o.key}
                                                data-file-key={o.key}
                                                className={[
                                                    'flex cursor-pointer items-start gap-3 border-l-[3px] px-4 py-2.5 transition-colors',
                                                    checked
                                                        ? 'border-l-blue-500 bg-blue-50 dark:bg-blue-950/30'
                                                        : active
                                                            ? 'border-l-gray-500 bg-gray-100 dark:bg-slate-800'
                                                            : 'border-l-transparent hover:bg-gray-50 dark:hover:bg-slate-800/50',
                                                ].join(' ')}
                                                onClick={() => onSelectKey(o.key)}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    className="mt-0.5 shrink-0"
                                                    aria-label={`select-${file}`}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        onToggleKey(o.key, e.target.checked);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                                <div className="min-w-0 flex-1">
                                                    <button
                                                        type="button"
                                                        className="max-w-full truncate text-left text-sm font-medium text-gray-900 dark:text-slate-100 hover:underline"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onOpenPreview(o.key);
                                                        }}
                                                    >
                                                        {file}
                                                    </button>
                                                    <div className="truncate text-[11px] text-gray-400 dark:text-slate-500">
                                                        {o.key}
                                                    </div>
                                                </div>
                                                <div className="shrink-0 text-right text-xs">
                                                    <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-slate-500">
                                                        {o.logFolder || getLogFolderFromKey(o.key) || '—'}
                                                    </div>
                                                    <div className="text-gray-600 dark:text-slate-400">
                                                        {formatLogBytes(o.sizeBytes)}
                                                    </div>
                                                    <div className="text-gray-500 dark:text-slate-500">
                                                        {formatApiDateTime(o.lastModifiedEpochMs)}
                                                    </div>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </TableLoadingOverlay>
                    </div>

                    {/* ── Download banner (pinned to bottom) ── */}
                    <div className="shrink-0 border-t border-slate-300 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800 dark:to-slate-900 px-4 py-3">
                        <div className="flex items-center gap-3">
                            <p className="min-w-0 flex-1 text-[11px] text-gray-400 dark:text-slate-500">
                                {busyBulkDownload
                                    ? `Downloading ${bulkProgress.completed} / ${bulkProgress.total}…`
                                    : hasSelection
                                        ? `${selectedKeys.size} file${selectedKeys.size !== 1 ? 's' : ''} selected — drag to select more, or use checkboxes`
                                        : 'Select files above, or drag across rows to select a range'}
                            </p>
                            {onDelete ? (
                                <button
                                    type="button"
                                    className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-700/60 dark:bg-slate-900 dark:text-red-300 dark:hover:bg-red-950/30"
                                    onClick={onDelete}
                                    disabled={busyBulkDelete || busyBulkDownload || !hasSelection}
                                >
                                    {busyBulkDelete ? (
                                        <InlineSpinner size="xs" variant="current" />
                                    ) : (
                                        <Trash2 className="h-4 w-4 shrink-0" aria-hidden />
                                    )}
                                    {busyBulkDelete ? 'Deleting…' : hasSelection ? `Delete (${selectedKeys.size})` : 'Delete'}
                                </button>
                            ) : null}
                            <button
                                type="button"
                                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                                onClick={onDownload}
                                disabled={busyBulkDownload || busyBulkDelete || !hasSelection}
                            >
                                {busyBulkDownload ? (
                                    <InlineSpinner size="xs" variant="current" />
                                ) : (
                                    <Download className="h-4 w-4 shrink-0" aria-hidden />
                                )}
                                {busyBulkDownload
                                    ? 'Downloading…'
                                    : hasSelection
                                        ? `Download (${selectedKeys.size})`
                                        : 'Download'}
                            </button>
                        </div>
                        {busyBulkDownload && (
                            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-blue-100 dark:bg-blue-900/40">
                                <div
                                    className="h-full rounded-full bg-blue-600 transition-all"
                                    style={{
                                        width:
                                            bulkProgress.total > 0
                                                ? `${Math.min(100, Math.round((bulkProgress.completed / bulkProgress.total) * 100))}%`
                                                : '0%',
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </PortaledLayer>
    );
}
